import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const RESERVATION_MS = 10 * 60 * 1000;
const STORAGE_KEY = 'cm-cart-reservation-expiry';

/**
 * Shows a live "Your cart is reserved for MM:SS" countdown for as long as
 * the cart has items. The expiry timestamp is persisted in localStorage so
 * it survives reloads/new tabs — it starts fresh the moment the cart goes
 * from empty to non-empty, and clears the moment it goes back to empty (or
 * the countdown reaches zero).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} time
 *
 * @extends {Component<Refs>}
 */
class CartReservationTimer extends Component {
  requiredRefs = ['time'];

  /** @type {number | undefined} */
  #intervalId;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#sync(this.getAttribute('cart-item-count') !== '0');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#stopTicking();
  }

  /**
   * @param {CustomEvent & {promise?: Promise<any>}} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;
        this.#sync(itemCount > 0);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-reservation-timer] Event promise rejected:', error);
      });
  };

  /**
   * @param {boolean} hasItems
   */
  #sync(hasItems) {
    if (!hasItems) {
      localStorage.removeItem(STORAGE_KEY);
      this.#stopTicking();
      this.hidden = true;
      return;
    }

    let expiry = Number(localStorage.getItem(STORAGE_KEY));
    if (!expiry || expiry <= Date.now()) {
      expiry = Date.now() + RESERVATION_MS;
      localStorage.setItem(STORAGE_KEY, String(expiry));
    }

    this.hidden = false;
    this.#startTicking(expiry);
  }

  /**
   * @param {number} expiry
   */
  #startTicking(expiry) {
    this.#stopTicking();

    const tick = () => {
      const remainingMs = expiry - Date.now();
      if (remainingMs <= 0) {
        this.#sync(false);
        return;
      }
      const totalSeconds = Math.ceil(remainingMs / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      this.refs.time.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
    };

    tick();
    this.#intervalId = window.setInterval(tick, 1000);
  }

  #stopTicking() {
    if (this.#intervalId !== undefined) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = undefined;
    }
  }
}

if (!customElements.get('cart-reservation-timer')) {
  customElements.define('cart-reservation-timer', CartReservationTimer);
}
